<template>
    <div class="modal is-active" v-if="show">
        <div class="modal-background" @click="cancel"></div>
        <div class="modal-card kb-ann-modal">
            <header class="modal-card-head">
                <p class="modal-card-title">
                    {{ $t('knowledgebase.announcementCreate') }}
                </p>
                <button class="delete" @click.prevent="cancel"></button>
            </header>
            <section class="modal-card-body">
                <p class="kb-ann-modal__blurb">
                    {{ $t('knowledgebase.announcementCreateBlurb') }}
                </p>

                <div class="field">
                    <label class="label">{{ $t('knowledgebase.announcementLabel') }}</label>
                    <div class="control">
                        <input
                            ref="labelInput"
                            class="input"
                            type="text"
                            v-model="label"
                            :placeholder="$t('knowledgebase.announcementLabelPlaceholder')"
                            @keydown.enter.prevent="submit"
                        />
                    </div>
                </div>

                <div class="kb-ann-modal__dates">
                    <div class="field">
                        <label class="label">{{ $t('knowledgebase.announcementStarts') }}</label>
                        <div class="control">
                            <input class="input" type="date" v-model="validFrom" />
                        </div>
                    </div>
                    <div class="field">
                        <label class="label">{{ $t('knowledgebase.announcementEnds') }}</label>
                        <div class="control">
                            <input class="input" type="date" v-model="validTo" />
                        </div>
                    </div>
                </div>

                <p
                    v-if="dayCount != null"
                    class="kb-ann-modal__day-count"
                >
                    {{ $tc('knowledgebase.announcementDayCount', dayCount, {count: dayCount}) }}
                </p>

                <p v-if="error" class="kb-ann-modal__error">
                    {{ error }}
                </p>
            </section>
            <footer class="modal-card-foot is-clearfix">
                <button
                    class="button is-primary"
                    :class="{'is-loading': busy}"
                    :disabled="!canSubmit || busy"
                    @click="submit"
                >
                    {{ $t('knowledgebase.create') }}
                </button>
                <button class="button" @click="cancel">
                    {{ $t('generic.cancel') }}
                </button>
            </footer>
        </div>
    </div>
</template>

<script>
function todayISO() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function addDaysISO(iso, days) {
    if(!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + days);
    const yy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
}

export default {
    name: 'kb-announcement-create-modal',

    data() {
        return {
            show: false,
            busy: false,
            label: '',
            validFrom: '',
            validTo: '',
            error: '',
            resolver: null,
        };
    },

    computed: {
        canSubmit() {
            if(!this.validFrom || !this.validTo) return false;
            return this.validFrom <= this.validTo;
        },
        dayCount() {
            if(!this.validFrom || !this.validTo) return null;
            if(this.validFrom > this.validTo) return null;
            const [y1, m1, d1] = this.validFrom.split('-').map(Number);
            const [y2, m2, d2] = this.validTo.split('-').map(Number);
            const a = new Date(y1, m1 - 1, d1);
            const b = new Date(y2, m2 - 1, d2);
            return Math.round((b - a) / 86400000) + 1;
        },
    },

    methods: {
        // The caller passes a submitter(payload) that returns a promise.
        // We keep the modal open while it runs, surface errors inline, and
        // close on success. Resolves with true on a successful create, null
        // on cancel — saves the caller a separate close() ceremony.
        open({submitter}) {
            this.label = '';
            this.validFrom = todayISO();
            this.validTo = addDaysISO(this.validFrom, 7);
            this.error = '';
            this.busy = false;
            this.show = true;
            this._submitter = submitter;
            this.$nextTick(() => {
                if(this.$refs.labelInput) this.$refs.labelInput.focus();
            });
            return new Promise(resolve => {
                this.resolver = resolve;
            });
        },
        async submit() {
            if(!this.canSubmit || this.busy) return;
            this.error = '';
            this.busy = true;
            const payload = {
                label: this.label.trim() || null,
                valid_from: this.validFrom,
                valid_to: this.validTo,
            };
            try {
                await this._submitter(payload);
                this.show = false;
                const r = this.resolver;
                this.resolver = null;
                if(r) r(true);
            } catch (e) {
                this.error = (e && e.message) || this.$t('generic.unknownError');
            } finally {
                this.busy = false;
            }
        },
        cancel() {
            this.show = false;
            this.busy = false;
            const r = this.resolver;
            this.resolver = null;
            if(r) r(null);
        },
    },
};
</script>

<style lang="scss" scoped>
.kb-ann-modal {
    max-width: 480px;
    margin: 0 auto;

    &__blurb {
        margin-bottom: 14px;
        color: #666;
        font-size: 0.9rem;
    }

    &__dates {
        display: flex;
        gap: 12px;

        .field {
            flex: 1;
        }
    }

    &__day-count {
        margin-top: -4px;
        font-size: 0.85rem;
        color: #888;
    }

    &__error {
        margin-top: 10px;
        color: #c0392b;
        font-size: 0.9rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-announcement-create-modal.vue